import { ethers } from "ethers";
import { BASE_SEPOLIA_TOKENS, EVM_CONFIG, type TokenInfo } from "../../config";
import { getBaseReadProvider } from "../../utils/base-read-provider";

const storageKey = `stealthos:tokens:v1:${EVM_CONFIG.CHAIN_ID}`;
const pending = new Map<string, Promise<TokenInfo>>();
const erc20 = new ethers.Interface([
  "function decimals() view returns(uint8)",
  "function symbol() view returns(string)",
  "function name() view returns(string)",
  "function balanceOf(address) view returns(uint256)",
]);
export function savedTokenAddresses(): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(storageKey) || "[]");
    return Array.isArray(value)
      ? value
          .filter(
            (x): x is string => typeof x === "string" && ethers.isAddress(x),
          )
          .slice(0, 200)
      : [];
  } catch {
    return [];
  }
}
export function saveTokenAddress(address: string) {
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify(
        [
          ...new Set([...savedTokenAddresses(), ethers.getAddress(address)]),
        ].slice(-200),
      ),
    );
  } catch {
    /* Import still works when browser storage is unavailable. */
  }
}

/** Metadata comes from this chain; names/symbols are untrusted display text. */
export function resolveTokenInfo(value: string): Promise<TokenInfo> {
  const address = ethers.getAddress(value);
  const configured = BASE_SEPOLIA_TOKENS.find(
    (t) => t.address.toLowerCase() === address.toLowerCase(),
  );
  if (configured) return Promise.resolve(configured);
  const old = pending.get(address);
  if (old) return old;
  const operation = (async () => {
    const provider = getBaseReadProvider();
    if (
      Number((await provider.getNetwork()).chainId) !== EVM_CONFIG.CHAIN_ID ||
      (await provider.getCode(address)) === "0x"
    )
      throw Error("Enter an ERC-20 contract on the selected network.");
    const read = async (name: string, args: unknown[] = []) => {
      const raw = await provider.call({
        to: address,
        data: erc20.encodeFunctionData(name, args),
      });
      try {
        return erc20.decodeFunctionResult(name, raw)[0];
      } catch {
        if (
          (name === "symbol" || name === "name") &&
          ethers.isHexString(raw, 32)
        )
          return ethers.decodeBytes32String(raw);
        throw Error("This token's metadata could not be read.");
      }
    };
    const [decimals, symbol, name] = await Promise.all([
      read("decimals"),
      read("symbol").catch(() => address.slice(0, 8)),
      read("name").catch(() => "Custom token"),
      read("balanceOf", [ethers.ZeroAddress]),
    ]);
    const precision = Number(decimals);
    if (!Number.isInteger(precision) || precision < 0 || precision > 80)
      throw Error("This token's precision is not supported.");
    const clean = (text: unknown, size: number) =>
      String(text)
        .replace(/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g, "")
        .trim()
        .slice(0, size);
    return {
      address,
      symbol: clean(symbol, 32) || address.slice(0, 8),
      name: clean(name, 80) || "Custom token",
      decimals: precision,
      isNative: false,
      tokenType: "erc20" as const,
    };
  })();
  pending.set(address, operation);
  void operation.catch(() => pending.delete(address));
  return operation;
}
